import { useMemo, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import Login from './components/Login';
import AdsList from './components/AdsList';
import AdDetail from './components/AdDetail';
import Admin from './components/Admin';

// Theme configuration based on environment variable
const getThemeConfig = (themeName: string) => {
  if (themeName === 'adpercepta') {
    return {
      primary: {
        main: '#ac4a95',
        light: '#c068ad',
        dark: '#8a3a77',
      },
      secondary: {
        main: '#ac4a95',
      },
      background: {
        default: '#282828',
        paper: '#3a3a3a',
      },
      text: {
        primary: '#ffffff',
        secondary: '#d0d0d0',
      },
      buttonColor: '#ac4a95',
      buttonHoverColor: '#c068ad',
      gradientStart: '#282828',
      gradientEnd: 'rgba(172, 74, 149, 0)',
    };
  }

  // Default theme
  return {
    primary: {
      main: '#0540f2',
      light: '#3765f0',
      dark: '#0336c2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#00071B',
      paper: '#041852',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b0c4de',
    },
    buttonColor: '#679BF2',
    buttonHoverColor: '#5587E0',
    gradientStart: '#00071B',
    gradientEnd: 'rgba(5, 64, 242, 0)',
  };
};

function App() {
  const themeName = process.env.REACT_APP_THEME || 'default';

  const theme = useMemo(() => {
    const colors = getThemeConfig(themeName);

    return createTheme({
  palette: {
    primary: colors.primary,
    secondary: colors.secondary,
    background: colors.background,
    text: colors.text,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '24px',
          textTransform: 'none',
          fontWeight: 500,
        },
        contained: {
          backgroundColor: colors.buttonColor,
          color: '#ffffff',
          borderRadius: '24px',
          border: `2px solid ${colors.buttonColor}`,
          '&:hover': {
            backgroundColor: colors.buttonHoverColor,
            borderColor: colors.buttonHoverColor,
          },
          '&.Mui-disabled': {
            backgroundColor: '#9e9e9e',
            borderColor: '#9e9e9e',
            color: '#e0e0e0',
          },
        },
        outlined: {
          backgroundColor: 'transparent',
          borderColor: colors.buttonColor,
          color: colors.buttonColor,
          borderRadius: '24px',
          border: `2px solid ${colors.buttonColor}`,
          '&:hover': {
            borderColor: colors.buttonHoverColor,
            backgroundColor: themeName === 'adpercepta'
              ? 'rgba(172, 74, 149, 0.08)'
              : 'rgba(103, 155, 242, 0.08)',
            color: colors.buttonHoverColor,
          },
          '&.Mui-disabled': {
            borderColor: '#9e9e9e',
            color: '#9e9e9e',
            backgroundColor: 'transparent',
          },
        },
        text: {
          backgroundColor: 'transparent',
          color: colors.buttonColor,
          borderRadius: '24px',
          border: '2px solid transparent',
          '&:hover': {
            backgroundColor: themeName === 'adpercepta'
              ? 'rgba(172, 74, 149, 0.08)'
              : 'rgba(103, 155, 242, 0.08)',
            borderColor: colors.buttonColor,
          },
          '&.Mui-disabled': {
            color: '#9e9e9e',
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          '&:hover': {
            backgroundColor: themeName === 'adpercepta'
              ? 'rgba(172, 74, 149, 0.2)'
              : 'rgba(55, 101, 240, 0.2)',
          },
          '&.Mui-disabled': {
            color: '#9e9e9e',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.paper,
          color: '#ffffff',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: themeName === 'adpercepta'
            ? 'linear-gradient(to bottom, rgba(172, 74, 149, 0.95) 0%, rgba(40, 40, 40, 0.85) 100%)'
            : 'linear-gradient(to bottom, rgba(103, 155, 242, 0.95) 0%, rgba(2, 19, 64, 0.85) 100%)',
          backgroundImage: themeName === 'adpercepta'
            ? 'linear-gradient(to bottom, rgba(172, 74, 149, 0.95) 0%, rgba(40, 40, 40, 0.85) 100%)'
            : 'linear-gradient(to bottom, rgba(103, 155, 242, 0.95) 0%, rgba(2, 19, 64, 0.85) 100%)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          '& .MuiDialogTitle-root .MuiIconButton-root': {
            border: '2px solid #ffffff',
            borderRadius: '50%',
            backgroundColor: 'transparent',
            color: '#ffffff',
            padding: '4px',
            width: '34px',
            height: '34px',
            '& .MuiSvgIcon-root': {
              fontSize: '16px',
            },
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderColor: '#ffffff',
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.paper,
          color: '#ffffff',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.paper,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            color: '#ffffff',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.23)',
            },
            '&:hover fieldset': {
              borderColor: colors.primary.main,
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.primary.main,
            },
          },
          '& .MuiInputLabel-root': {
            color: colors.text.secondary,
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: themeName === 'adpercepta'
              ? 'rgba(172, 74, 149, 0.12)'
              : 'rgba(5, 64, 242, 0.12)',
          },
          '&.Mui-selected': {
            backgroundColor: themeName === 'adpercepta'
              ? 'rgba(172, 74, 149, 0.16)'
              : 'rgba(5, 64, 242, 0.16)',
            '&:hover': {
              backgroundColor: themeName === 'adpercepta'
                ? 'rgba(172, 74, 149, 0.24)'
                : 'rgba(5, 64, 242, 0.24)',
            },
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: `linear-gradient(180deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`,
          backgroundColor: colors.background.default,
          minHeight: '100vh',
          color: '#ffffff',
        },
      },
    },
  },
});
  }, [themeName]);

  // Set CSS custom properties for use in CSS files
  useEffect(() => {
    const colors = getThemeConfig(themeName);
    const root = document.documentElement;

    root.style.setProperty('--primary-color', colors.primary.main);
    root.style.setProperty('--background-default', colors.background.default);
    root.style.setProperty('--background-paper', colors.background.paper);
    root.style.setProperty('--gradient-start', colors.gradientStart);
    root.style.setProperty('--gradient-end', colors.gradientEnd);

    // Update favicon dynamically
    const faviconLink = document.getElementById('favicon') as HTMLLinkElement;
    if (faviconLink) {
      if (themeName === 'adpercepta') {
        faviconLink.href = '/adpercepta.png';
        faviconLink.type = 'image/png';
      } else {
        faviconLink.href = '/logo.svg';
        faviconLink.type = 'image/svg+xml';
      }
    }
  }, [themeName]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/ads" element={<AdsList />} />
            <Route path="/ad/:adName" element={<AdDetail />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/" element={<Navigate to="/login" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
